import {IdcardOutlined, LogoutOutlined, SettingOutlined, SwapOutlined, UserOutlined,} from '@ant-design/icons';
import {history, useModel} from '@umijs/max';
import type {MenuProps} from 'antd';
import {Spin} from 'antd';
import {createStyles} from 'antd-style';
import React, {useState} from 'react';
import {flushSync} from 'react-dom';
import {outLogin} from '@/services/login/api';
import {Key, removeCookie} from '@/utils/cookie';
import HeaderDropdown from '../HeaderDropdown';
import PersonalInfoModal from './PersonalInfoModal';
import SwitchIdentityModal from './SwitchIdentityModal';
import UpdatePasswordModal from './UpdatePasswordModal';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const {initialState} = useModel('@@initialState');
  const {currentUser} = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({token}) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
                                                                   menu,
                                                                   children,
                                                                 }) => {
                                                                  
  const [personalInfoModalOpen, setPersonalInfoModalOpen] = useState<boolean>(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
  const [identityModalOpen, setIdentityModalOpen] = useState<boolean>(false);

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    await outLogin();
    removeCookie(Key.ATK);
    removeCookie(Key.UIK);
    const {search, pathname} = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const {styles} = useStyles();

  const {initialState, setInitialState} = useModel('@@initialState');


  const onMenuClick: MenuProps['onClick'] = (event) => {
    const {key} = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({...s, currentUser: undefined}));
      });
      loginOut();
      return;
    }
    if (key === 'personal-info') {
      setPersonalInfoModalOpen(true);
      return;
    }
    if (key === 'update-password') {
      setPasswordModalOpen(true);
      return;
    }
    if (key === 'switch-identity') {
      setIdentityModalOpen(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const {currentUser} = initialState;

  if (!currentUser || !currentUser.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
        {
          key: 'center',
          icon: <UserOutlined/>,
          label: '个人中心',
        },
        {
          key: 'settings',
          icon: <SettingOutlined/>,
          label: '个人设置',
        },
        {
          type: 'divider' as const,
        },
      ]
      : []),
    ...(currentUser.identities && currentUser.identities.length > 1
      ? [
          {
            key: 'switch-identity',
            icon: <SwapOutlined />,
            label: '切换身份',
          },
        ]
      : []),
    {
      key: 'personal-info',
      icon: <IdcardOutlined />,
      label: '个人信息',
    },
    {
      key: 'update-password',
      icon: <UserOutlined/>,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined/>,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>

      <PersonalInfoModal
        open={personalInfoModalOpen}
        onOpenChange={setPersonalInfoModalOpen}
      />

      <UpdatePasswordModal
        open={passwordModalOpen}
        onOpenChange={setPasswordModalOpen}
      />

      <SwitchIdentityModal
        open={identityModalOpen}
        onOpenChange={setIdentityModalOpen}
      />
    </>
  );
};
